class AgeVerifier extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'beyours:age-verifier';

    this.classes = {
      bodyClass: 'promo-popup--open',
      activeClass: 'is-active',
      closingClass: 'is-closing',
      showImage: 'show-image',
      blurImage: 'promo-popup--blur'
    };

    this.blurImage = this.dataset.blurImage === 'true';

    this.popup = this.querySelector('.promo-popup');
    this.dialog = this.popup.querySelector('.popup');
    this.onModalKeydown = this.handleModalKeydown.bind(this);
    this.onModalFocus = this.handleModalFocus.bind(this);
    this.inertElements = new Map();
    this.modalOpen = false;

    if (!this.getCookie(this.cookieName) || this.dataset.testMode === 'true') {
      this.init();
    }

    const button = this.querySelector('button');
    if (button) button.addEventListener('click', this.close.bind(this));
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  disconnectedCallback() {
    this.releaseModal();
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);

      document.body.classList.remove(this.classes.bodyClass);
      this.blurImage && document.body.classList.remove(this.classes.blurImage);
    }
  }
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  init() {
    if (Shopify && Shopify.designMode) {
      return;
    }

    this.open();
  }

  open() {
    if (this.modalOpen) return;
    clearTimeout(this.closeTimer);
    document.body.classList.remove(this.classes.bodyClass);
    this.blurImage && document.body.classList.remove(this.classes.blurImage);

    this.popup.classList.add(this.classes.activeClass);
    this.popup.classList.remove(this.classes.closingClass);
    this.activateModal();
    setTimeout(() => {
      this.popup.classList.add(this.classes.showImage);
    }, 600);

    if (this.popup.dataset.position === 'center') {
      setScrollbarWidth();
      document.body.classList.add(this.classes.bodyClass);
      this.blurImage && document.body.classList.add(this.classes.blurImage);
    }
  }

  close() {
    this.popup.classList.add(this.classes.closingClass);
    this.blurImage && document.body.classList.remove(this.classes.blurImage);

    this.closeTimer = setTimeout(() => {
      this.popup.classList.remove(this.classes.activeClass);
      this.popup.classList.remove(this.classes.closingClass);
      this.popup.classList.remove(this.classes.showImage);
      this.releaseModal();

      if (this.popup.dataset.position === 'center') {
        document.body.classList.remove(this.classes.bodyClass);
      }
    }, 500);

    // Remove a cookie in case it was set in test mode
    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.cookieName);
      return;
    }

    this.setCookie(this.cookieName, this.dataset.expiry);
  }

  activateModal() {
    if (this.modalOpen) return;
    this.modalOpen = true;
    this.previousFocus = document.activeElement;
    this.dialog.setAttribute('role', 'dialog');
    this.dialog.setAttribute('aria-modal', 'true');
    this.dialog.setAttribute('tabindex', '-1');
    const heading = this.dialog.querySelector('.popup__heading, .popup__subheading, .popup__text');
    if (heading) {
      if (!heading.id) heading.id = `AgeVerifierHeading-${this.dataset.sectionId}`;
      this.dialog.setAttribute('aria-labelledby', heading.id);
    }
    this.isolateBackground();
    this.backgroundObserver = new MutationObserver(() => this.isolateBackground());
    this.backgroundObserver.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', this.onModalKeydown, true);
    document.addEventListener('focusin', this.onModalFocus, true);
    requestAnimationFrame(() => {
      if (this.modalOpen) this.dialog.focus({ preventScroll: true });
    });
  }

  isolateBackground() {
    let branch = this.popup;
    while (branch && branch !== document.body) {
      const parent = branch.parentElement;
      if (!parent) break;
      for (const sibling of parent.children) {
        if (sibling === branch || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
        if (!this.inertElements.has(sibling)) this.inertElements.set(sibling, sibling.inert);
        sibling.inert = true;
      }
      branch = parent;
    }
  }

  modalControls() {
    return [...this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter(element => !element.closest('[inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  }

  handleModalKeydown(event) {
    if (!this.modalOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = this.modalControls();
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first) {
      event.preventDefault();
      this.dialog.focus();
    } else if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  handleModalFocus(event) {
    if (this.modalOpen && !this.dialog.contains(event.target)) this.dialog.focus({ preventScroll: true });
  }

  releaseModal() {
    if (!this.modalOpen) return;
    this.modalOpen = false;
    this.backgroundObserver?.disconnect();
    document.removeEventListener('keydown', this.onModalKeydown, true);
    document.removeEventListener('focusin', this.onModalFocus, true);
    for (const [element, wasInert] of this.inertElements) element.inert = wasInert;
    this.inertElements.clear();
    this.dialog.removeAttribute('aria-modal');
    let target = this.previousFocus;
    if (!target?.isConnected || target === document.body || target.closest('[inert]') || !target.getClientRects().length) target = document.querySelector('main, #MainContent');
    if (target) {
      const temporaryTabindex = !target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea');
      if (temporaryTabindex) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (temporaryTabindex) target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0`;
  }
}
customElements.define('age-verifier', AgeVerifier);
